import COUNTRY_CURRENCY from "./countryCurrency";

export type NumberFormatConfig = {
  currency: string;
  locale: string;
};

const DEFAULT_COUNTRY = "IN";

const FALLBACK_FORMAT: NumberFormatConfig = { currency: "USD", locale: "en-US" };

let activeCountry = DEFAULT_COUNTRY;

export const setActiveCountry = (country?: string): void => {
  activeCountry = country || DEFAULT_COUNTRY;
};

const findCountry = (code?: string) =>
  Object.values(COUNTRY_CURRENCY).find((record) => record.alpha2 === code);

export const getCountryFormat = (country?: string): NumberFormatConfig => {
  const record = findCountry(country || activeCountry);

  if (!record) {
    return FALLBACK_FORMAT;
  }

  return { currency: record.currency, locale: `en-${record.alpha2}` };
};

const formatNumber = (
  number: number,
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 2 },
  locale?: string
): string => {
  const mapped = options.currency ? findCountry(options.currency) : undefined;
  const resolvedOptions = {
    ...options,
    currency: mapped?.currency ?? options.currency,
  };
  const resolvedLocale =
    locale ?? (mapped ? `en-${mapped.alpha2}` : getCountryFormat().locale);

  try {
    return Intl.NumberFormat(resolvedLocale, resolvedOptions).format(number);
  } catch {
    return Intl.NumberFormat(FALLBACK_FORMAT.locale, {
      ...options,
      currency: FALLBACK_FORMAT.currency,
    }).format(number);
  }
};

export const formatCurrencyValue = (
  number: number,
  options: Intl.NumberFormatOptions = {},
  country?: string
): string => {
  const { currency, locale } = getCountryFormat(country);

  return formatNumber(
    number,
    {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 2,
      ...options,
    },
    locale
  );
};

export default formatNumber;
